"use client"

import React, { createContext, useContext, useState, useCallback } from 'react';

interface LoadingState {
  id: string;
  message: string;
  itemId?: string;
  userId: string;
  userEmail?: string;
}

interface LoadingContextType {
  loadingStates: LoadingState[];
  addLoadingState: (state: Omit<LoadingState, 'id'>) => string;
  removeLoadingState: (id: string) => void;
  removeLoadingStateByItemId: (itemId: string) => void;
  isItemLoading: (itemId: string) => boolean;
  getLoadingMessage: (itemId: string) => string | null;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

export function LoadingProvider({ children }: { children: React.ReactNode }) {
  const [loadingStates, setLoadingStates] = useState<LoadingState[]>([]);

  const addLoadingState = useCallback((state: Omit<LoadingState, 'id'>) => {
    const id = `${state.userId}-${state.itemId || 'general'}-${Date.now()}`;
    const newState: LoadingState = { ...state, id };
    
    setLoadingStates(prev => [...prev, newState]);
    return id;
  }, []);

  const removeLoadingState = useCallback((id: string) => {
    setLoadingStates(prev => prev.filter(state => state.id !== id));
  }, []);

  const removeLoadingStateByItemId = useCallback((itemId: string) => {
    setLoadingStates(prev => prev.filter(state => state.itemId !== itemId));
  }, []);

  const isItemLoading = useCallback((itemId: string) => {
    return loadingStates.some(state => state.itemId === itemId);
  }, [loadingStates]);

  const getLoadingMessage = useCallback((itemId: string) => {
    const state = loadingStates.find(s => s.itemId === itemId);
    return state?.message || null;
  }, [loadingStates]);

  return (
    <LoadingContext.Provider value={{
      loadingStates,
      addLoadingState,
      removeLoadingState,
      removeLoadingStateByItemId,
      isItemLoading,
      getLoadingMessage
    }}>
      {children}
    </LoadingContext.Provider>
  );
}

export function useLoading() {
  const context = useContext(LoadingContext);
  if (context === undefined) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
} 